import { Search, User, Clock } from "lucide-react";
import { useTranslation } from "react-i18next";

import { Badge } from "@/shared/components/ui/badge";
import { useFormatters } from "@/shared/lib/formatting";
import { useLabRequests } from "@/admin/hooks/use-lab-tasks";

/**
 * Why this claim has an inspection, shown on the Inspection tab.
 *
 * WPS-175 #5: the tab listed the linked inspection's code and status and
 * nothing else, so an inspector could see that a visit had been asked for but
 * not what they were being asked to look at. The reason and the requester's
 * notes were recorded on the lab request and rendered only on the Lab tab -
 * a tab an inspector has no reason to open.
 *
 * Reads the same lab-request records the Lab tab does, filtered to inspection
 * requests, so there is one source of truth for the request and no copy to
 * fall out of date.
 */
/**
 * Readable form of a reason key the tenant's option list does not carry a
 * label for. A reason picked from the dropdown always resolves a label; this
 * only catches keys written by an import or an older configuration, which
 * would otherwise surface raw as "sample_inconclusive".
 */
function humaniseReason(reason: string): string {
  if (!reason) return "";
  const words = reason.replace(/[_-]+/g, " ").trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

export function InspectionRequestsCard({ claimUuid }: { claimUuid: string }) {
  const { t } = useTranslation();
  const { data: requests = [] } = useLabRequests(claimUuid);
  const { formatDateTime } = useFormatters();

  const inspectionRequests = requests.filter(
    (r) => r.request_type === "INSPECTION",
  );

  // Nothing was requested: render nothing rather than an empty card. An
  // inspection can also be raised directly, and a "no requests" panel on
  // those would read as though something were missing.
  if (inspectionRequests.length === 0) return null;

  return (
    <section className="overflow-hidden rounded-[8px] border-[0.5px] border-[var(--rule)] bg-[var(--bg-light)]">
      <div className="flex flex-col gap-4 p-5 sm:p-6">
        <div className="flex items-center gap-2">
          <Search className="h-3.5 w-3.5 text-[var(--ink-3)]" />
          <h3 className="text-[11px] font-medium uppercase tracking-[0.08em] text-[var(--ink-2)]">
            {t(
              "claims.inspectionRequests.title",
              "Why an inspection was requested",
            )}
          </h3>
          <Badge variant="outline" className="text-[9px]">
            {inspectionRequests.length}
          </Badge>
        </div>

        <div className="flex flex-col gap-3">
          {inspectionRequests.map((request) => (
            <article
              key={request.uuid}
              className="flex flex-col gap-2 rounded-[6px] border-[0.5px] border-[var(--rule)] bg-[var(--bg)] p-4"
            >
              <div className="flex flex-wrap items-start justify-between gap-2">
                <span className="text-[13px] font-medium text-[var(--ink)]">
                  {request.reason_label ||
                    humaniseReason(request.reason) ||
                    t(
                      "claims.inspectionRequests.fallbackLabel",
                      "Inspection requested",
                    )}
                </span>
                <Badge
                  variant={
                    request.status === "COMPLETED" ? "secondary" : "outline"
                  }
                  className="text-[9px] uppercase tracking-[0.06em]"
                >
                  {request.status}
                </Badge>
              </div>

              {/* The requester's own words. This is the sentence an inspector
                  actually needs before setting out. */}
              {request.notes && (
                <p className="text-[13px] leading-[1.55] text-[var(--ink-2)]">
                  {request.notes}
                </p>
              )}

              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-[var(--ink-3)]">
                {request.requested_by_name && (
                  <span className="inline-flex items-center gap-1">
                    <User className="h-3 w-3" />
                    {request.requested_by_name}
                  </span>
                )}
                <span className="inline-flex items-center gap-1">
                  <Clock className="h-3 w-3" />
                  {formatDateTime(request.created_at)}
                </span>
              </div>

              {request.response_notes && (
                <p className="rounded-[4px] bg-[var(--bg-subtle)] px-3 py-2 text-[12px] text-[var(--ink-2)]">
                  {request.response_notes}
                </p>
              )}
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}
